import { DSFlex, type DSIconName, DSText } from '@zoe/ds-web/next-gen'
import { useContext } from 'react'
import type { ResponsiveImageType } from 'react-datocms'

import type { FragmentComponent } from '@/types/graphql'

import { TrackingContext } from '@/context/trackingContext'

import type { CtaRecord } from '@/components/blocks/Shared/CtaCollectionBlock'
import type { ContentCta } from '@/components/patterns/content'
import { Cta } from '@/components/patterns/Cta'
import { PdpHero, type PdpHeroConfig, type PdpHeroSlideChange } from '@/components/patterns/PdpHero'

import { responsiveImageBlock } from '@/lib/api/graphql/utils/commonBlocks'
import { trackAccordionClickEvent, trackCtaClick } from '@/lib/gtm/gtmV2'

type GalleryImage = { responsiveImage: ResponsiveImageType }

/** The Dato `simple_pricing` block: a plain price + note + CTAs payment section. */
export type SimplePricingRecord = {
  __typename: 'SimplePricingRecord'
  id: string
  price?: string | null
  priceNote?: string | null
  ctas: CtaRecord[]
}

/** The Dato `product_hero` block. Pure content — the payment slot is a block of its own, so other PDPs can plug theirs in. */
export type ProductHeroRecord = {
  __typename: 'ProductHeroRecord'
  title: string
  highlight?: string | null
  description?: string | null
  socialProofText?: string | null
  /** Polymorphic payment slot — extend the union as other PDP payment blocks join. */
  payment?: SimplePricingRecord | null
  /** Lead image first — the pattern keeps the order for the bento and the swiper alike. */
  images: GalleryImage[]
  bentoImages: GalleryImage[]
  socialProofAvatars: GalleryImage[]
  features: { icon: string; label: string }[]
  details: { question: string; answer: string }[]
}

export const productHeroFragment = `
  fragment ProductHeroFragment on ProductHeroRecord {
    __typename
    title
    highlight
    description
    socialProofText
    payment {
      __typename
      ... on SimplePricingRecord {
        id
        price
        priceNote
        ctas {
          __typename
          id
          label
          url
          newTab
          variant
          colorScheme
          dataTestId
        }
      }
    }
    images {
      ${responsiveImageBlock({ width: 1400 })}
    }
    bentoImages {
      ${responsiveImageBlock({ width: 1400 })}
    }
    socialProofAvatars {
      ${responsiveImageBlock({ width: 112, height: 112 })}
    }
    features {
      __typename
      id
      icon
      label
    }
    details {
      __typename
      id
      question
      answer
    }
  }
`

/** Local copy until the shared adapter mappers land — see ContentContainerBlock. */
const toContentCta = (cta: CtaRecord): ContentCta => ({
  label: cta.label,
  url: cta.url,
  newTab: cta.newTab,
  type: cta.variant === 'secondary' || cta.variant === 'tertiary' ? cta.variant : 'primary',
  colorScheme:
    cta.colorScheme === 'brand' || cta.colorScheme === 'accent' || cta.colorScheme === 'contrast'
      ? cta.colorScheme
      : undefined,
  testId: cta.dataTestId ?? undefined,
})

const toImage = ({ responsiveImage }: GalleryImage) => ({
  src: responsiveImage.src ?? '',
  alt: responsiveImage.alt ?? '',
})

/** Pure: the CMS shape in, the pattern's config out. */
export const toPdpHeroConfig = (record: ProductHeroRecord): PdpHeroConfig => ({
  images: record.images.map(toImage),
  bentoImages: record.bentoImages.map(toImage),
  socialProof:
    record.socialProofText || record.socialProofAvatars.length > 0
      ? { avatars: record.socialProofAvatars.map(toImage), text: record.socialProofText ?? '' }
      : undefined,
  title: record.title,
  highlight: record.highlight ?? undefined,
  description: record.description ?? undefined,
  features: record.features.map(({ icon, label }) => ({ icon: icon as DSIconName, label })),
  details: record.details.map(({ question, answer }) => ({ title: question, body: answer })),
})

const HeroPayment = ({ price, note, ctas }: { price?: string | null; note?: string | null; ctas: ContentCta[] }) => (
  <DSFlex direction="column" gap="betweenDifferent" width="100%">
    <DSFlex direction="column" gap="betweenSimilar" width="100%">
      {price && (
        <DSText variant="titleXS" color="strong">
          {price}
        </DSText>
      )}
      {note && (
        <DSText variant="paragraphSCaption" color="default">
          {note}
        </DSText>
      )}
    </DSFlex>
    {ctas.map((cta) => (
      <Cta key={cta.label} cta={cta} />
    ))}
  </DSFlex>
)

export const ProductHeroBlock: FragmentComponent<{ onSlideChange?: PdpHeroSlideChange }, ProductHeroRecord> = ({
  record,
  onSlideChange,
}) => {
  const { section } = useContext(TrackingContext)
  const trackingSection = section ?? 'cms'

  const withTracking = (cta: ContentCta): ContentCta => ({
    ...cta,
    onClick: () => trackCtaClick(trackingSection, cta.testId || cta.url || cta.label, { link_position: 'page_body' }),
  })

  const paymentSlot =
    record.payment?.__typename === 'SimplePricingRecord' ? (
      <HeroPayment
        price={record.payment.price}
        note={record.payment.priceNote}
        ctas={record.payment.ctas.map(toContentCta).map(withTracking)}
      />
    ) : undefined

  return (
    <PdpHero
      config={toPdpHeroConfig(record)}
      paymentSlot={paymentSlot}
      onSlideChange={onSlideChange}
      onDetailToggle={(title) =>
        trackAccordionClickEvent({
          section: trackingSection,
          linkPosition: 'pdp_hero_details',
          textOrImageUrl: title,
        })
      }
    />
  )
}

ProductHeroBlock.fragment = productHeroFragment
ProductHeroBlock.recordName = 'ProductHeroRecord'
